<template>
  <content-subpage
    class="joined-community-listing-content-subpage"
    :visible="true"
    :has-parent-subpage="true"
    :titles="titles"
    :back-label="t('profile_back_to_profile_label')"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <div class="content">
        <joined-community-card-list
          :has-joined-communities="hasJoinedCommunities"
          :paginated-community-list="paginatedCommunityList"
          :show-placeholder-browse-community-text="false"
        />
        <cv-pagination
          v-if="hasJoinedCommunities"
          :number-of-items="totalRecords"
          :page-sizes="[pageSize]"
          :page="pageNo"
          @change="handlePageChange"
        />
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import JoinedCommunityCardList from "@modules/student-group/components/JoinedCommunityCardList.vue";
import StudentGroupApi from "@apis/student.group.api";
import { isEmpty } from "@utils/var.check";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@/plugins/common/composables/error";
import { useContentSubpage } from "@/plugins/content-subpage/composables/content-subpage";

// Composables
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

// Data
const joinedCommunityList = ref([]);
const pageNo = ref(1);

// Props
interface Props {
  params: Record<string, unknown>;
}
const props = defineProps<Props>();

const { titles, actionHidden, defaultExpose, actionBack } = useContentSubpage(props.params);

// Computed
const pageSize = computed(() => {
  return !isEmpty(props.params.pageSize) ? props.params.pageSize : 12;
});

const hasJoinedCommunities = computed(() => {
  return joinedCommunityList.value.length > 0;
});

const totalRecords = computed(() => {
  return joinedCommunityList.value.length;
});

const paginatedCommunityList = computed(() => {
  const start = (pageNo.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return joinedCommunityList.value.slice(start, end);
});

const userId = computed(() => {
  return props.params.userId;
});

// Methods
const fetchStudentGroups = function () {
  StudentGroupApi.fetchStudentGroupList({
    allRoles: true,
    roles: "",
    publicDomain: true,
    userId: userId.value,
  }).then((response) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
    }

    joinedCommunityList.value = response.data;
  }, passiveApiFailHandler);
};

const handlePageChange = function (event) {
  pageNo.value = event.page;
};

onMounted(() => {
  // Created
  titles.value.push(t("profile_subpage_contribution_communities_joined"));
  fetchStudentGroups();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.joined-community-listing-content-subpage {
  .bx--modal-container {
    > .content {
      background-color: $ui-01;
    }
  }
  .joined-community-card-list {
    .card-component {
      @include media("desktop") {
        width: calc(50% - #{$spacing-03});
      }
    }
  }
  .bx--pagination {
    .bx--select-input {
      background-color: transparent;
    }
  }
}
</style>
